/**
 * i18n 工具函数
 *
 * @desc 用于在非React组件中获取多语言文本
 */

// 多语言文本映射
const MESSAGE_MAP = {
  'zh-CN': {
    // 网络请求相关
    loginExpired: '登录已过期，请重新登录',
    networkError: '网络请求失败，请稍后重试',
    httpError: '请求错误！',
    resourceRemoved: '资源已被移除',
    redirect: '重定向',
    resourceNotModified: '资源没有被修改',
    parameterError: '参数列表错误（缺少，格式不匹配）',
    loginStatusExpired: '登录状态已过期',
    accessDenied: '访问受限，授权过期',
    resourceNotFound: '资源，服务未找到',
    methodNotAllowed: '不允许的http方法',
    resourceConflict: '资源冲突，或者资源被锁',
    unsupportedMediaType: '不支持的数据，媒体类型',
    internalError: '系统内部错误',
    notImplemented: '接口未实现',
    gatewayError: '网关错误',
    serviceUnavailable: '服务不可用',
    gatewayTimeout: '网关超时',
    systemWarning: '系统警告消息',
  },
  'en-US': {
    // 网络请求相关
    loginExpired: 'Login expired, please login again',
    networkError: 'Network request failed, please try again later',
    httpError: 'Request Error!',
    resourceRemoved: 'Resource has been removed',
    redirect: 'Redirect',
    resourceNotModified: 'Resource not modified',
    parameterError: 'Parameter list error (missing, format mismatch)',
    loginStatusExpired: 'Login status expired',
    accessDenied: 'Access denied, authorization expired',
    resourceNotFound: 'Resource, service not found',
    methodNotAllowed: 'HTTP method not allowed',
    resourceConflict: 'Resource conflict, or resource locked',
    unsupportedMediaType: 'Unsupported data, media type',
    internalError: 'Internal system error',
    notImplemented: 'Interface not implemented',
    gatewayError: 'Gateway error',
    serviceUnavailable: 'Service unavailable',
    gatewayTimeout: 'Gateway timeout',
    systemWarning: 'System warning message',
  },
};

/**
 * 获取当前语言
 */
export const getCurrentLocale = (): string => {
  return localStorage.getItem('umi_locale') || 'en-US';
};

/**
 * 设置当前语言
 * @param locale 语言
 */
export const setCurrentLocale = (locale: string): void => {
  localStorage.setItem('umi_locale', locale);
};

/**
 * 获取多语言文本
 * @param key 文本键值
 * @returns 对应的多语言文本
 */
export const getMessage = (key: string): string => {
  const locale = getCurrentLocale();
  const messages = MESSAGE_MAP[locale as keyof typeof MESSAGE_MAP] || MESSAGE_MAP['en-US'];
  return messages[key as keyof typeof messages] || key;
};

/**
 * 获取HTTP错误信息
 * @param code HTTP状态码
 * @returns 对应的错误信息
 */
export const getHttpMessage = (code: number): string => {
  const messageMap: Record<number, string> = {
    301: getMessage('resourceRemoved'),
    303: getMessage('redirect'),
    304: getMessage('resourceNotModified'),
    400: getMessage('parameterError'),
    401: getMessage('loginStatusExpired'),
    403: getMessage('accessDenied'),
    404: getMessage('resourceNotFound'),
    405: getMessage('methodNotAllowed'),
    409: getMessage('resourceConflict'),
    415: getMessage('unsupportedMediaType'),
    500: getMessage('internalError'),
    501: getMessage('notImplemented'),
    502: getMessage('gatewayError'),
    503: getMessage('serviceUnavailable'),
    504: getMessage('gatewayTimeout'),
    601: getMessage('systemWarning'),
  };

  return messageMap[code] || getMessage('httpError');
};

/**
 * 格式化语言
 * @param language 语言 如：zh_CN -> zh-CN
 * @returns 格式化后的语言
 */
export const formatLanguage = () => {
  const locale = localStorage.getItem('umi_locale') || 'en-US';
  return locale.replace('-', '_');
};
